import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type {
  KioskFooterCartBlockConfig,
  KioskFooterCartBlockScenarioContent,
} from '@front-platform/content-builder';
import { kioskFooterCartBlockResolveConfig } from '@front-platform/content-builder';
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  input,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { BasketStoreRefiner } from '@common/refiners/basket.refiner';
import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import {
  CartItemKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { ShopKeys } from '@config/keys/shop.keys';
import { Paths } from '@config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { DoodReconstructedCart, DoodReconstructedCartItem } from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CartService } from '@core/services/cart/cart.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import {
  ConfirmModalComponent,
  ConfirmModalConfig,
} from '@shared/modals/confirm-modal/confirm-modal.component';
import {
  ItemEditModalComponent,
  ItemEditModalConfig,
} from '@shared/modals/item-edit-modal/item-edit-modal.component';
import { combineLatest, filter, map, shareReplay, Subject, take, takeUntil, tap } from 'rxjs';
import { ResetStateService } from '@core/services/reset-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { KioskHorizontalScrollComponent } from '../kiosk-horizontal-scroll/kiosk-horizontal-scroll.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { CommonModule } from '@angular/common';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { KioskCurrencyDisclaimerComponent } from '@shared/atoms/kiosk/kiosk-currency-disclaimer/kiosk-currency-disclaimer.component';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { KioskProductOverviewCartAtomComponent } from '@shared/atoms/kiosk/kiosk-product-overview-cart-atom/kiosk-product-overview-cart-atom.component';
import { CallComponentMethodPipe } from '@shared/pipes/call-component-method/call-component-method.pipe';
import { CartUtils } from '@app/utilities/cart.util';
import { KIOSK_FOOTER_CART_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

const EMPTY_FOOTER_CART_PREVIEW: KioskFooterCartBlockScenarioContent = {
  reconstructedCart: [],
  reconstructedShareOrderBasket: [],
  finalPrice: 0,
};

function previewCartQuantity(content: KioskFooterCartBlockScenarioContent): number {
  return content.reconstructedCart.reduce(
    (total, cart) => total + cart.items.reduce((sum, line) => sum + line.item.quantity, 0),
    0,
  );
}

export interface KioskFooterCartBlockRuntimeConfig {}

export type KioskFooterCartBlockConfigInput = Partial<
  Omit<KioskFooterCartBlockConfig, keyof KioskFooterCartBlockRuntimeConfig> &
    KioskFooterCartBlockRuntimeConfig
>;
export type KioskFooterCartBlockResolvedConfig = Omit<
  KioskFooterCartBlockConfig,
  keyof KioskFooterCartBlockRuntimeConfig
> &
  Partial<KioskFooterCartBlockRuntimeConfig>;
export interface KioskFooterCartBlockContract extends ConfigComponentContract<
  KioskFooterCartBlockConfigInput,
  KioskFooterCartBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-footer-cart-block',
  templateUrl: './kiosk-footer-cart-block.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ContentBuilderColorPipe,
    PriceAtomComponent,
    KioskCurrencyDisclaimerComponent,
    ButtonAtomComponent,
    KioskHorizontalScrollComponent,
    KioskProductOverviewCartAtomComponent,
    CallComponentMethodPipe,
  ],
})
export class KioskFooterCartBlockComponent
  extends DestroyerBase
  implements OnInit, OnDestroy, KioskFooterCartBlockContract
{
  readonly config = input<KioskFooterCartBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskFooterCartBlockConfig,
    KioskFooterCartBlockRuntimeConfig
  >(kioskFooterCartBlockResolveConfig, () => ({ config: this.config() }));
  readonly primaryButtonBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'primary_button_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly primaryButtonTextColor = computed(
    () =>
      this.resolvedConfig()['primary_button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryButtonBackgroundColor = computed(
    () =>
      this.resolvedConfig()[
        'secondary_button_background_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryButtonTextColor = computed(
    () =>
      this.resolvedConfig()['secondary_button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productBackgroundColor = computed(
    () => this.resolvedConfig()['product_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productTextColor = computed(
    () => this.resolvedConfig()['product_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  /**
   * Older blocks have no saved top border. Their edge still follows the primary button
   * until a color is chosen in the content builder.
   */
  readonly topBorderColor = computed(() => {
    const chosen = this.config()?.top_border_color as { type?: string; value?: string } | undefined;
    if (chosen?.value && chosen.type && chosen.type.toLowerCase() !== 'default') {
      return chosen as IContentBuilderFieldColor;
    }
    return this.primaryButtonBackgroundColor();
  });

  @ViewChild('horizontalScroll') horizontalScroll: KioskHorizontalScrollComponent | undefined;

  @ViewChild('productList', { static: false }) productList!: ElementRef;

  basket$ = this.basketSelector.select;
  basketId$ = this.basketSelector.select.pipe(map(basket => basket[CartItemKeys.Id]));
  userId$ = this.authSelector.selectUserId;
  reconstructedCart$ = this.cartService.getCartLineItems$();
  orderValidationInProgress$ = this.orderSelector.selectIsValidating$;
  orderValidate$ = this.orderSelector.selectValidation$;
  reconstructedShareOrderBasket$ = this.basketRefiner.selectCartItemsGroupedByUser;
  order$ = this.orderSelector.selectValidation$;
  cartProducts$ = this.cartRefiner.selectActiveCartProducts;
  /** Total units in cart (sum of line quantities), not number of distinct lines */
  cartLineItemsTotalQuantity$ = this.cartRefiner.selectActiveCartProducts.pipe(
    map(products => CartUtils.sumActiveCartLineQuantities(products)),
  );

  isValidOrder$ = combineLatest({
    orderProducts: this.cartRefiner.selectActiveCartProducts,
    orderErrors: this.orderSelector.selectErrors,
  }).pipe(
    map(({ orderProducts, orderErrors }) => {
      return orderProducts.length > 0 && !orderErrors.violations.length;
    }),
  );

  shopKeys = ShopKeys;
  productKeys = ProductKeys;
  cartItemKeys = CartItemKeys;
  reconstructedCartKeys = ReconstructedCartKeys;
  reconstructedCartItemKeys = ReconstructedCartItemKeys;

  isNavigationToCartInProgress = false;

  errorMessages: string[] = [];
  orderIsValidating = false;
  isCartOverflow = false;

  public hideButtons = signal(false);

  private readonly navigationEndUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).url),
    ),
    {
      initialValue: this.router.url,
    },
  );

  protected readonly isAPageWhereToHideCartDetails = computed(() =>
    this.navigationEndUrl()?.includes('product-add'),
  );

  protected readonly hideCartDetails = computed(() => {
    return signal(this.isAPageWhereToHideCartDetails());
  });

  private destroyed$ = new Subject<boolean>();

  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(KIOSK_FOOTER_CART_BLOCK_COMPONENT);

  constructor(
    private orderSelector: OrderStoreSelector,
    private authSelector: AuthStoreSelector,
    private cartRefiner: CartStoreRefiner,
    private basketRefiner: BasketStoreRefiner,
    private basketSelector: BasketStoreSelector,
    private cartService: CartService,
    private router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly modalsService: ModalsService<[ConfirmModalConfig, ItemEditModalConfig]>,
    private readonly cartDispatcher: CartStoreDispatcher,
    private readonly errorService: ErrorService,
    private readonly resetStateService: ResetStateService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly translateService: TranslateService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
    if (this.previewState.isActive()) {
      this.bindPreviewCart();
      return;
    }
    this.orderDispatcher.checkCartIsValid();
  }

  ngOnInit(): void {
    if (this.previewState.isActive()) {
      this.reconstructedCart$
        .pipe(
          takeUntil(this.destroyed$),
          tap(() => this.scrollToRight()),
        )
        .subscribe();
      return;
    }

    this.cartService.loadShopsOfCart$().subscribe();

    this.orderSelector.selectIsValidating$
      .pipe(
        takeUntil(this.destroyed$),
        tap(orderIsValidating => {
          this.orderIsValidating = orderIsValidating;
        }),
      )
      .subscribe();

    this.errorService
      .getErrorMessage$()
      .pipe(
        takeUntil(this.destroyed$),
        tap(messages => (this.errorMessages = messages)),
      )
      .subscribe();

    this.checkRoute(this.router.url);

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.checkRoute(this.router.url);
    });

    this.cartProducts$
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => {
          this.scrollToRight();
        }),
      )
      .subscribe();
  }

  private bindPreviewCart(): void {
    const previewContent$ = this.previewScenario$.pipe(
      map(
        scenario =>
          previewScenarioData<KioskFooterCartBlockScenarioContent>(
            scenario,
            KIOSK_FOOTER_CART_BLOCK_COMPONENT,
          ) ?? EMPTY_FOOTER_CART_PREVIEW,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.reconstructedCart$ = previewContent$.pipe(
      map(content => content.reconstructedCart as unknown as DoodReconstructedCart[]),
    );
    this.reconstructedShareOrderBasket$ = previewContent$.pipe(
      map(content => content.reconstructedShareOrderBasket),
    ) as typeof this.reconstructedShareOrderBasket$;
    this.cartLineItemsTotalQuantity$ = previewContent$.pipe(
      map(content => previewCartQuantity(content)),
    );
    this.order$ = previewContent$.pipe(
      map(content => ({ final_price: content.finalPrice })),
    ) as typeof this.order$;
    this.orderValidationInProgress$ = previewContent$.pipe(
      map(() => false),
    ) as typeof this.orderValidationInProgress$;
  }

  private scrollToRight(): void {
    if (this.horizontalScroll) {
      this.horizontalScroll.scrollToRight();
    }
  }

  navigateToCart(): void {
    this.isNavigationToCartInProgress = true;
    this.router.navigate([this.routerHelper.translateRoute(Paths.CartResume)]);
  }

  ngOnDestroy(): void {
    this.modalsService.close<UntypedModalIO>(ItemEditModalComponent.handle);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private checkRoute(currentRoute: string): void {
    this.hideButtons?.set(currentRoute.includes('product-add'));
  }

  onUpdateItem(itemId: number | string): void {
    this.modalsService.open(ItemEditModalComponent.handle, undefined, {
      backgroundColor: this.productBackgroundColor(),
      textColor: this.productTextColor(),
      buttonBackgroundColor: this.primaryButtonBackgroundColor(),
      buttonTextColor: this.primaryButtonTextColor(),
    });
    this.cartService.loadShopsOfCart$().pipe(takeUntil(this.destroyed$)).subscribe();
    this.cartService
      .getCartLineItems$()
      .pipe(
        takeUntil(this.destroyed$),
        take(1),
        map(reconstructedCart => this.lookForItemInCart(itemId, reconstructedCart)),
        map(cartItem => {
          if (cartItem) {
            this.cartDispatcher.updateStore({ edit_item: cartItem });
          }
        }),
      )
      .subscribe();
  }

  private lookForItemInCart(
    itemId: string | number,
    reconstructedCart: DoodReconstructedCart[],
  ): null | DoodReconstructedCartItem {
    for (const cartOfShop of reconstructedCart) {
      const cartItem = cartOfShop[ReconstructedCartKeys.Items].find(
        elem => elem[ReconstructedCartItemKeys.Item][CartItemKeys.Id] === itemId,
      );
      if (cartItem) {
        return cartItem;
      }
    }

    return null;
  }

  clearCart(): void {
    this.modalsService.open(ConfirmModalComponent.handle, undefined, {
      backgroundColor: this.productBackgroundColor(),
      textColor: this.productTextColor(),
      buttonBackgroundColor: this.primaryButtonBackgroundColor(),
      buttonTextColor: this.primaryButtonTextColor(),
      title: this.translateService.instant('basket.clear-basket-confirm-modal.title'),
      message: this.translateService.instant('basket.clear-basket-confirm-modal.message'),
      confirmButtonText: this.translateService.instant('basket.clear-basket-confirm-modal.confirm'),
    });

    this.modalsService
      .event$(ConfirmModalComponent.handle)
      .pipe(
        takeUntil(this._destroyerRef),
        filter(event => event.type === 'confirm'),
        tap(() => {
          this.resetStateService.refreshUIAndResetLang();
        }),
      )
      .subscribe();
  }

  protected unAvailableProductById = toSignal(
    this.orderSelector.selectErrors.pipe(
      map(errors =>
        errors.violations
          ?.filter(
            violation =>
              violation.code &&
              [
                'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
                'PRODUCT_HIDDEN',
                'PRODUCT_NOT_AVAILABLE',
              ].includes(violation.code),
          )
          .map(violation => violation.data?.id as string)
          .reduce(
            (acc, id) => {
              acc[id] = true;
              return acc;
            },
            {} as Record<string, true>,
          ),
      ),
    ),
  );

  protected readonly mustReplaceAComposedProduct = (
    productId: string,
    items: DoodReconstructedCartItem[],
    unAvailableProductById: Record<string, true> | undefined,
  ): boolean => {
    if (!unAvailableProductById) return false;
    const productCartItem = items.find(cartItem => cartItem.item.item_id === productId);
    if (!productCartItem) {
      return false;
    }

    const composedProducts = productCartItem.item.products;
    if (!composedProducts) {
      return false;
    }
    const result = composedProducts.some(composedProduct => {
      if (unAvailableProductById[composedProduct.id]) {
        return true;
      }
      const hasUnAvailableAdditions = composedProduct.additions?.some(
        addition => unAvailableProductById[addition],
      );
      return hasUnAvailableAdditions;
    });

    return result;
  };

  protected toggleCartDetails(): void {
    this.hideCartDetails().update(state => !state);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskFooterCartBlockComponent, { 'keyAliases': {} });
